import { useCallback, useEffect, useMemo, useState } from "react";
import type { PhoneModelOption } from "@genz/platform";
import { useServices } from "../app/context.js";
import { loadCatalogList } from "../templates/catalogOptions.js";
import { notifyCatalogChanged } from "../templates/catalogInvalidation.js";
import { modelLabel } from "../templates/templateLabels.js";

/** The phone catalog — the one source of brands and models for every product picker. */
export function usePhoneCatalog() {
  const { client, logger } = useServices();
  const [models, setModels] = useState<PhoneModelOption[]>([]);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    const r = await loadCatalogList("phone_models", () => client.listPhoneModels(), logger);
    setModels(r.data);
    setError(!!r.error);
  }, [client, logger]);
  useEffect(() => { void reload(); }, [reload]);

  const brands = useMemo(
    () => [...new Set(models.map((m) => m.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [models],
  );

  const upsert = (m: PhoneModelOption) =>
    setModels((prev) => (prev.some((p) => p.id === m.id) ? prev.map((p) => (p.id === m.id ? m : p)) : [...prev, m]));

  const addModel = async (brand: string, model: string): Promise<PhoneModelOption> => {
    const created = await client.createPhoneModel({ brand, model });
    notifyCatalogChanged("phoneModel");
    upsert(created);
    return created;
  };

  /** A brand's models as select options; every model while no brand is chosen. */
  const optionsFor = (brand: string) =>
    models
      .filter((m) => !brand || m.brand.toLowerCase() === brand.toLowerCase())
      .map((m) => ({ value: m.id, label: modelLabel({ brand: m.brand, model: m.model }) }));

  return { models, error, reload, brands, optionsFor, addModel, upsert };
}
